import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import ConfirmDialog from '../common/ConfirmDialog';
import { functions, getTeamAllRepo, switchActiveRepo, getTeamActiveRepo, httpsCallable } from '../../firebase'
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import { MdGroup, MdCommit, MdInsertDriveFile, MdCode } from "react-icons/md";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MdWarningAmber, MdArrowDropDown, MdWarning, MdKeyboardArrowDown } from "react-icons/md";
import { FaSpinner } from "react-icons/fa"; // Make sure to install if not yet
import { TbReload } from "react-icons/tb";
import { Link } from 'react-router-dom';


const PRLinkSection = (props, ref) => {
    const { prLink, initialMeta, onChange, onMetaFetched, setRepoMatchHint, onFetchStart } = props;
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    //const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null); // new state
    const [shouldRenderMeta, setShouldRenderMeta] = useState(false);
    const { user, groupActiveRepo, groupMembers, setGroupActiveRepo, groupInfo } = useAuth();
    const [repositories, setRepositories] = useState([]);
    const [selectedRepoId, setSelectedRepoId] = useState("");
    const [formatError, setFormatError] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded((e) => !e);
    const [showSwitchRepoConfirm, setShowSwitchRepoConfirm] = useState(false);
    const [pendingSwitchRepoId, setPendingSwitchRepoId] = useState(null);
    const match = prLink.match(/github\.com\/(.+?)\/(.+?)\/pull\/(\d+)/);

    const PR_NOT_FOUND_ERROR = "❗️PR not found in public or connected repositories. If it is a private repo, make sure it set up and activate in repo page.";
    const INVALID_LINK_ERROR =
        "❗️Invalid GitHub PR link. Format must be: https://github.com/user/repo/pull/<PR Number>";
    const disabledReason = !match
        ? "Invalid GitHub PR link. Format must be: https://github.com/user/repo/pull/<PR Number>"
        : null;

    useEffect(() => {
        if (!prLink) {
            setFormatError(null);
            return; // allow empty
        }

        setFormatError(match ? null : INVALID_LINK_ERROR);
    }, [prLink, match]);

    useEffect(() => {
        if (initialMeta && !meta) {
            setMeta(initialMeta);
            setShouldRenderMeta(true); 
        }
    }, [initialMeta]);

    const error = formatError || fetchError;

    const fetchRepos = async () => {
        const all = await getTeamAllRepo(groupInfo.groupId);
        setRepositories(Object.values(all || {}));
    };

    const getRepoFromPRLink = (link) => {
        const match = link.match(/github\.com\/([^/]+\/[^/]+)\/pull\/\d+/);
        return match ? match[1] : null;
    };

    useEffect(() => {
        const prRepo = getRepoFromPRLink(prLink)?.toLowerCase();;
        const activeRepo = groupActiveRepo?.repo?.full_name?.toLowerCase();

        if (!prRepo || !activeRepo) {
            setRepoMatchHint(null);
        } else {
            setRepoMatchHint(prRepo === activeRepo ? "match" : "mismatch");
        }
        if (error === PR_NOT_FOUND_ERROR && user && groupInfo?.groupId) {
            fetchRepos();
        }

    }, [error, groupActiveRepo, prLink]);

    const resetPRFields = useCallback(() => {
        onMetaFetched(null);
        setShouldRenderMeta(false);
        setMeta(null);
    }, [onChange, onMetaFetched]);


    const findMentions = (text) => {
        if (!text) return [];

        const mentions = new Set();

        // 1. Match @username directly
        const atMatches = text.match(/@([a-zA-Z0-9-_]+)/g) || [];
        atMatches.forEach((m) => {
            const username = m.slice(1);
            if (username !== "users") {
                mentions.add(username);
            }
        });

        // 2. Match Co-authored-by: Name <username@users.noreply.github.com>
        const coAuthorRegex = /co-authored[- ]by:\s*.*?<([a-zA-Z0-9-_]+)@users\.noreply\.github\.com>/gi;
        let match;
        while ((match = coAuthorRegex.exec(text)) !== null) {
            mentions.add(match[1]);
        }

        return [...mentions];
    };

    const validateAndRenderPRMeta = (data, sourceLabel = "PR") => {
        const allHandles = [...(data.collaborators || [])];
        const groupHandles = groupMembers?.map(m => m.github_username)?.filter(Boolean) || [];
        const intersection = allHandles.filter(h => groupHandles.includes(h));

        if (intersection.length === 0) {
            setFetchError(`❗️This ${sourceLabel} was not submitted by a member of your group.`);
            return false;
        }

        setMeta(data);
        setShouldRenderMeta(true);
        onMetaFetched?.(data);
        return true;
    };


    const handleFetch = async () => {
        if (!match) {
            setMeta(null);
            console.error("Invalid GitHub PR link format:", prLink);
            setFetchError("❗️Invalid GitHub PR link. Format must be: https://github.com/user/repo/pull/<PR Number>");
            return { ok: false };
        }

        const [, owner, repo, prNumber] = match;
        setNotice(null);
        setLoading(true);
        setFetchError(null);
        setShouldRenderMeta(false);

        try {
            //try public repo
            const publicData = await fetchPublic(owner, repo, prNumber);
            if (publicData) {
                const valid = validateAndRenderPRMeta(publicData, "public PR");
                return valid ? { ok: true, meta: publicData } : { ok: false }; // stop here, whether it renders or shows error
            }


            //try private repo
            const installationId = groupActiveRepo?.installation_id;
            const backendData = await fetchPRMetaFromBackend(prLink, installationId);
            if (backendData) {
                const valid = validateAndRenderPRMeta(backendData, "private PR");
                return valid ? { ok: true, meta: backendData } : { ok: false }; // stop here too
            }

            //both fail
            setFetchError(PR_NOT_FOUND_ERROR);
            return { ok: false };
        }
        catch (error) {
            console.error("Error fetching PR info:", error);
            setFetchError("❗️Failed to fetch PR info. Please check the link or try again later.");
            return { ok: false, error };
        } finally {
            setLoading(false);
        }
    };

    const fetchPublic = async (owner, repo, prNumber) => {
        try {

            const prRes = await fetch(`https://api.github.com/repos/${owner}/${repo}/pulls/${prNumber}`);
            if (prRes.status !== 200) {
                const errText = await prRes.text();
                console.warn(`PR fetch failed with status ${prRes.status}: ${errText}`);
                return null;
            }

            if (prRes.status === 200) {
                const prData = await prRes.json();
                const commitsRes = await fetch(`https://api.github.com/repos/${owner}/${repo}/pulls/${prNumber}/commits`);
                const commitsData = await commitsRes.json();

                const collaborators = new Set();
                const coAuthors = new Set();

                // Extract from PR body (@mention usernames)
                findMentions(prData.body).forEach((m) => coAuthors.add(m));


                commitsData.forEach(commit => {
                    // Author GitHub usernames
                    if (commit.author?.login) collaborators.add(commit.author.login);

                    // Co-authors from commit message
                    findMentions(commit.commit.message).forEach((m) => coAuthors.add(m));
                });


                return {
                    source: "public",
                    title: prData.title,
                    body: prData.body,
                    changedFiles: prData.changed_files,
                    additions: prData.additions,
                    deletions: prData.deletions,
                    commitCount: commitsData.length,
                    collaborators: Array.from(collaborators), // Github Username
                    coAuthors: Array.from(coAuthors), // Raw co-author name + email
                    state: prData.state,
                };
            }

        } catch (err) {
            console.warn("Public fetch failed, trying GitHub App...");
        }
    };

    const fetchPRMetaFromBackend = async (prLink, installationId = null) => {
        try {
            const getPRMeta = httpsCallable(functions, "getPRMeta");

            const response = await getPRMeta({
                pr_link: prLink, installation_id: installationId
            });

            return response.data; // contains PR metadata
        } catch (error) {
            console.error("Backend PR fetch failed:", error);
            throw error;
        }
    };

    const resolveNameParts = (username, members = []) => {
        const match = members.find(m => m.github_username === username);
        return {
            name: match?.name || 'Unauthorized User',
            handle: username
        };
    };

    // Expose the method to parent
    useImperativeHandle(ref, () => ({
        fetchNow: handleFetch,     // parent can call ref.current.fetchNow()
        reset: () => {             // (optional) expose other actions
            setMeta(null);
            setFetchError(null);
        },
    }), [handleFetch]);



    // Define PRMetaCard component with custom equality check
    const PRMetaCard = React.memo(function PRMetaCard({ title, body, commits, filesChanged, codeChanges, collaborators, coAuthors, state, onRemove }) {
        const scrollRef = useRef();
        const lastScroll = useRef(0);
        const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
        const filteredCoAuthors = coAuthors?.filter(
            (user) => !collaborators.includes(user)
        );

        useEffect(() => {
            const el = scrollRef.current;
            if (!el) return;

            const saveScroll = () => {
                lastScroll.current = el.scrollTop;
            };

            el.addEventListener('scroll', saveScroll);
            return () => el.removeEventListener('scroll', saveScroll);
        }, []);

        function cleanPRBody(rawBody) {
            if (typeof rawBody !== 'string') rawBody = '';  // handle null/undefined/non-string
            return rawBody
                .replace(/(?<!\n)(Co-authored[- ]by:)/g, '\n$1')
                .replace(/<!--[\s\S]*?-->/g, '')
                .replace(/[ \t]+$/gm, '')
                .trim();
        }


        return (
            <>
            <Card $status={state}>
                <TitleRow>
                    <Label>PR Title</Label>
                    <PRLinkAnchor href={prLink} target="_blank" rel="noopener noreferrer">
                        View on GitHub
                    </PRLinkAnchor>
                </TitleRow>
                <Header>{title}</Header>

                <Section>
                    <Label>PR Description</Label>
                    <DescriptionBox ref={scrollRef}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            breaks={true}
                            components={{
                                code({ node, inline, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || '');
                                    return !inline && match ? (
                                        <SyntaxHighlighter
                                            style={oneDark}
                                            language={match[1]}
                                            PreTag="div"
                                            {...props}
                                        >
                                            {String(children).replace(/\n$/, '')}
                                        </SyntaxHighlighter>
                                    ) : (
                                        <code className={className} {...props}>
                                            {children}
                                        </code>
                                    );
                                }
                            }}
                        >
                            {cleanPRBody(body)}
                        </ReactMarkdown>
                    </DescriptionBox>
                </Section>

                <Section>
                    <Label>PR Metadata</Label>

                    <MetaGrid>
                        <MetaBox color={{ bg: "#eff6ff", border: "#bfdbfe", text: "#3b82f6", value: "#1d4ed8" }}>
                            <IconWrapper><MdCommit className="text-blue-600 mr-3" size={24} /></IconWrapper>
                            <MetaText color={{ text: "#3b82f6", value: "#1d4ed8" }}>
                                <p>Commits</p>
                                <p>{commits}</p>
                            </MetaText>
                        </MetaBox>

                        <MetaBox color={{ bg: "#ecfdf5", border: "#a7f3d0", text: "#10b981", value: "#047857" }}>
                            <IconWrapper><MdInsertDriveFile className="text-green-600 mr-3" size={24} /></IconWrapper>
                            <MetaText color={{ text: "#10b981", value: "#047857" }}>
                                <p>Files Changed</p>
                                <p>{filesChanged}</p>
                            </MetaText>
                        </MetaBox>

                        <MetaBox color={{ bg: "#f5f3ff", border: "#ddd6fe", text: "#8b5cf6", value: "#6d28d9" }}>
                            <IconWrapper><MdCode className="text-purple-600 mr-3" size={24} /></IconWrapper>
                            <MetaText color={{ text: "#8b5cf6", value: "#6d28d9" }}>
                                <p>Code Changes</p>
                                <CodeChangeLine>
                                    <Green>+{codeChanges.additions}</Green>
                                    <Divider>/</Divider>
                                    <Red>-{codeChanges.deletions}</Red>
                                </CodeChangeLine>
                            </MetaText>
                        </MetaBox>

                        <MetaBox color={{ bg: "#fef2f2", border: "#fecaca", text: "#dc2626", value: "#991b1b" }}>
                            <IconWrapper><MdWarningAmber size={24} /></IconWrapper>
                            <MetaText color={{ text: "#dc2626", value: "#991b1b" }}>
                                <p>Status</p>
                                {state === "open" ? (
                                    <Green>Open</Green>
                                ) : (
                                    <Red>Closed</Red>
                                )}
                            </MetaText>
                        </MetaBox>

                        <MetaBox full color={{ bg: "#fef9c3", border: "#fde68a", text: "#b45309", value: "#ca8a04" }}>
                            <IconWrapper><MdGroup className="text-yellow-600 mr-3" size={24} /></IconWrapper>
                            <MetaText color={{ text: "#fdb833", value: "#ca8a04" }}>
                                <p>
                                    Code Contributor{collaborators.length !== 1 ? 's' : ''}
                                </p>
                                <CollaboratorList>
                                    {collaborators.map((username, idx) => {
                                        const { name, handle } = resolveNameParts(username, groupMembers);
                                        return (
                                            <CollaboratorBlock key={idx}>
                                                <CollaboratorName>{name}</CollaboratorName>
                                                <CollaboratorHandle
                                                    href={`https://github.com/${username}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {handle}
                                                </CollaboratorHandle>
                                            </CollaboratorBlock>
                                        );
                                    })}
                                </CollaboratorList>
                            </MetaText>
                        </MetaBox>

                        {filteredCoAuthors?.length > 0 && (
                            <MetaBox full color={{ bg: "#fefce8", border: "#fef08a", text: "#eab308", value: "#a16207" }}>
                                <IconWrapper><MdGroup className="text-yellow-600 mr-3" size={24} /></IconWrapper>
                                <MetaText color={{ text: "#facc15", value: "#ca8a04" }}>
                                    <p>
                                        Participant{filteredCoAuthors.length !== 1 ? 's' : ''}
                                    </p>
                                    <CollaboratorList>
                                        {filteredCoAuthors.map((username, idx) => {
                                            const { name, handle } = resolveNameParts(username, groupMembers);
                                            return (
                                                <CollaboratorBlock key={idx}>
                                                    <CollaboratorName>{name}</CollaboratorName>
                                                    <CollaboratorHandle
                                                        href={`https://github.com/${username}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        @{handle}
                                                    </CollaboratorHandle>
                                                </CollaboratorBlock>
                                            );
                                        })}
                                    </CollaboratorList>
                                </MetaText>
                            </MetaBox>
                        )}
                    </MetaGrid>
                </Section>
                <Section>
                    <RemoveButtonWrapper>
                        <RemoveButton
                            type="button"
                            onClick={() => setShowRemoveConfirm(true)}
                        >
                            Remove PR
                        </RemoveButton>
                    </RemoveButtonWrapper>
                </Section>
            </Card>
            {showRemoveConfirm && (
                <ConfirmDialog
                    title="Remove PR?"
                    message="Are you sure you want to remove this PR info? This action cannot be undone."
                    confirmText="Yes, Remove"
                    cancelText="No, Go Back"
                    variant="danger"
                    onConfirm={() => { setShowRemoveConfirm(false); onRemove(); }}
                    onCancel={() => setShowRemoveConfirm(false)}
                />
            )}
            </>
        );
    });


    return (
        <PRSection>
            <PRInputRow>
                <PRInput
                    type="url"
                    placeholder="https://github.com/user/repo/pull/123"
                    value={prLink}
                    onChange={(e) => {
                        onChange(e.target.value);
                    }}
                />
                <IconButton
                    type="button"
                    onClick={() => {
                        if (onFetchStart) onFetchStart();
                        handleFetch();
                    }}
                    disabled={loading || !!formatError || !match}
                    title={disabledReason || "Fetch PR Info"}
                    aria-disabled={loading || !match}
                >
                    {loading ? (
                        <FaSpinner className="spin" />
                    ) : !match ? (
                        <MdWarning />
                    ) : (
                        <TbReload />
                    )}
                </IconButton>
            </PRInputRow>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {notice && <WarningMessage>{notice}</WarningMessage>}

            {error === PR_NOT_FOUND_ERROR && (
                <Card>
                    <WarningBox>
                        <WarningIcon />
                        <WarningText>
                            <p>
                                If this PR is from a <Strong>private repo</Strong>, make sure:
                            </p>
                            <List>
                                <li>
                                    The repo is <Strong>connected</Strong> in the{" "}
                                    <Link
                                        to="/repo-setup"
                                        style={{ textDecoration: "underline", color: "#2563eb", fontWeight: 500 }}
                                    >
                                        Repo Connection Page
                                    </Link>
                                </li>
                                <li>
                                    You have <Strong>activated</Strong> that repo
                                </li>
                            </List>
                        </WarningText>
                    </WarningBox>

                    <Section>
                        <SectionTitle>Current Active Repo:</SectionTitle>
                        <Paragraph
                            style={{
                                color: groupActiveRepo?.repo?.full_name ? "#22c55e" : "#ef4444",
                            }}
                        >
                            {groupActiveRepo?.repo?.full_name || "None selected"}
                        </Paragraph>
                    </Section>

                    <Section>
                        <SectionTitle>Switch Active Repo</SectionTitle>
                        <Label htmlFor="connected-repositories">Connected Repositories:</Label>
                        <FlexRow>
                            <SelectWrapper>
                                <Select
                                    id="repoSelect"
                                    value={selectedRepoId}
                                    onChange={(e) => setSelectedRepoId(e.target.value)}
                                >
                                    <option value="" disabled>Select a repo</option>
                                    {repositories.map((entry) => (
                                        <option key={entry.repo.id} value={entry.repo.id}>
                                            {entry.repo.full_name}
                                        </option>
                                    ))}
                                </Select>
                                <DropdownIcon size={24} />
                            </SelectWrapper>
                            <SwitchButton
                                disabled={!selectedRepoId || selectedRepoId === groupActiveRepo?.repo?.id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (selectedRepoId === groupActiveRepo?.repo?.id) return;
                                    setPendingSwitchRepoId(selectedRepoId);
                                    setShowSwitchRepoConfirm(true);
                                }}
                            >
                                Switch Repo
                            </SwitchButton>

                        </FlexRow>
                    </Section>
                </Card >
            )}

            {shouldRenderMeta && meta && (
                <ToggleButton
                    type="button"
                    onClick={toggleExpanded}
                    aria-expanded={expanded}
                    aria-controls={`pr-card`}
                    title={expanded ? "Collapse details" : "Expand details"}
                >
                    <ChevronIcon $open={expanded} />
                    <span>{expanded ? "Hide" : "Show"} fetched details</span>
                </ToggleButton>
            )}


            {
                shouldRenderMeta && meta && (
                    <Collapsible
                        id={`gdoc-card`}
                        aria-hidden={!expanded}
                        $open={expanded}
                    >
                        <PRMetaCard
                            title={meta.title}
                            body={meta.body}
                            commits={meta.commitCount}
                            filesChanged={meta.changedFiles}
                            codeChanges={{
                                additions: meta.additions,
                                deletions: meta.deletions
                            }}
                            collaborators={meta.collaborators}
                            coAuthors={meta.coAuthors}
                            state={meta.state}
                            onRemove={() => {
                                resetPRFields();
                                setFetchError(null);
                                onChange("");
                            }}
                        />
                    </Collapsible>
                )
            }
            {showSwitchRepoConfirm && (
                <ConfirmDialog
                    title="Switch Repository?"
                    message="Are you sure you want to switch the active repo? This will affect PR validation and linking."
                    confirmText="Yes, Switch"
                    cancelText="No, Go Back"
                    variant="primary"
                    onConfirm={async () => {
                        setShowSwitchRepoConfirm(false);
                        await switchActiveRepo(groupInfo.groupId, pendingSwitchRepoId);
                        const updatedActive = await getTeamActiveRepo(groupInfo.groupId);
                        setGroupActiveRepo(updatedActive);
                        resetPRFields();
                        setFetchError(null);
                        setNotice("You've switched the active repository. Please re-fetch the PR info.");
                        setPendingSwitchRepoId(null);
                    }}
                    onCancel={() => { setShowSwitchRepoConfirm(false); setPendingSwitchRepoId(null); }}
                />
            )}
        </PRSection>
    );
}

export default React.memo(React.forwardRef(PRLinkSection));

const PRSection = styled.div`
    margin-top: 8px;
    margin-bottom: 8px;
`;

const PRInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PRInput = styled.input`
  flex: 1;
  padding: 12px 16px; /* Match vertical padding with the button */
  height: 48px; /* Optional, for consistency */
  font-size: 14px;
  border-radius: 10px;
  border: 1px solid rgba(234, 235, 234, 1);
  transition: border-color 0.2s ease;

   &:focus {
    outline: none;
    border: 2px solid #534af5;

  }
`;

const Card = styled.div`
  background: white;
  padding: 2rem 2rem 0rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin: 1rem auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  background-color: "#ffffff"; 
   

  border: 1px solid
    ${({ $status }) =>
        $status === "open"
            ? "#86efac"       // green border
            : $status === "closed"
                ? "#ddd6fe"       // purple border
                : "#e5e7eb"};     // default gray

`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const PRLinkAnchor = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0b99ff;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280; /* Tailwind gray-500 */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DescriptionBox = styled.div`
  max-height: 200px;
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 1rem;
  line-height: 1.625;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overscroll-behavior: contain;
  overflow-y: auto;

    /* Add spacing for markdown elements */
  p, ul, ol, pre, blockquote, table {
    margin-bottom: 1em;
  }

  code {
    background-color: #f3f4f6;
    padding: 0.2em 0.4em;
    border-radius: 4px;
  }

  pre {
    padding: 1em;
    background-color: #111827;
    color: #f3f4f6;
    border-radius: 6px;
    overflow: auto;
  }

  li {
    margin-left: 1.5em;
  }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'full',
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const MetaText = styled.div`
  p:first-child {
    font-size: 0.875rem;
    color: ${({ color }) => color.text};
    font-weight: 500;
  }
  p:last-child {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const CollaboratorList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CollaboratorBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollaboratorName = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937; /* Tailwind gray-800 */
`;

const CollaboratorHandle = styled.a`
  font-size: 0.875rem;
  color: #2563eb; /* Tailwind blue-600 */
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CodeChangeLine = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Green = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #16a34a;
`;

const Red = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
`;

const Divider = styled.span`
  color: #6d28d9; /* match purple if needed */
  padding: 0 0.2rem; /* adjust spacing */
  font-weight: 700;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

const WarningText = styled.div`
  color: #b45309;
  font-size: 0.875rem;
`;

const Strong = styled.span`
  font-weight: 600;
`;

const List = styled.ol`
  margin-top: 0.25rem;
  list-style: decimal inside;
  font-size: 0.875rem;
  color: #b45309;

  li + li {
    margin-top: 0.25rem;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  flex-grow: 1;
`;

const Select = styled.select`
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  font-size: 1rem;
  color: #374151;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
  }
`;

const WarningBox = styled.div`
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  align-items: start;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Paragraph = styled.p`
  color: #374151;
  font-size: 1rem;
  font-weight: 500;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DropdownIcon = styled(MdArrowDropDown)`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
`;

const SwitchButton = styled.button`
  height: 2.5rem;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #4f46e5;
  border: none;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;

  &:hover {
    background-color: #4338ca;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.5);
  }
`;

const WarningMessage = styled.div`
  background-color: #fef3c7;
  color: #92400e;
  padding: 12px;
  border-radius: 6px;
  margin-top: 8px;
  font-size: 14px;
`;

const WarningIcon = styled(MdWarningAmber)`
  font-family: 'Material Icons';
  font-size: 24px;
  color: #f59e0b; /* Tailwind's text-yellow-500 */
  vertical-align: middle;
`;


const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const RemoveButton = styled.button`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #fca5a5;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #fecaca;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5);
  }
`;


const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  border-radius: 10px;
  border: 1px solid #e4e4e4;
  background: #534af5;
  cursor: pointer;
  color: white;
  padding: 12px;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover { 
    background: #3f35f6ff;
 }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg { width: 22px; height: 22px; transition: transform 0.2s ease;}

  &:hover svg {
    transform: scale(1.2); /* zoom only the icon */
  }

  .spin {
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); } to { transform: rotate(360deg); }
  }
`;

const Collapsible = styled.div.withConfig({
    shouldForwardProp: (p) => p !== '$open',
})`
  overflow: hidden;
  transition: max-height 280ms ease, opacity 200ms ease;
  max-height: ${({ $open }) => ($open ? '2400px' : '0px')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  will-change: max-height, opacity;
`;

// Toggle button next to reload
const ToggleButton = styled.button`
  display: flex;
  margin-top: 8px;
  border: none;
  align-items: center;
  color: #534af5; /* text-gray-600 */
  background-color: #f3f4f6; /* bg-gray-100 */
  padding: 0.5rem 1rem; /* py-2 px-4 */
  border-radius: 0.5rem; /* rounded-lg */
  transition: color 0.2s ease;

  &:hover {
    color: #352cedff; /* hover:text-gray-800 */
  }

  span { 
    margin-left: 0.25rem; /* ml-2 */
    font-size: 0.875rem; /* text-sm */
    font-weight: 500; /* font-medium */ }
`;

const ChevronIcon = styled(MdKeyboardArrowDown).withConfig({
    shouldForwardProp: (p) => p !== '$open',
})`
  margin-left: -4px;
  font-size: 24px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;



